import React, { useCallback, useEffect, useRef } from "react";
import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";
import * as styles from "web/react/pages/product/product-image-gallery/product-image-gallery-fullscreen/product-image-gallery-fullscreen.css";

const MAX_ZOOM = 2;
const DRAG_THRESHOLD_PX = 5;

export enum ZoomDirection {
    in = "in",
    out = "out",
}
export interface ZoomableSlideProps {
    index: number;
    currentSlideIndex: number;
    onZoom: (direction: ZoomDirection) => void;
    onZoomStateChange: (isZoomed: boolean) => void;
    onClick: () => void;
    isDesktop?: boolean;
    children: React.ReactNode;
}

export function ZoomableSlide({
    index,
    currentSlideIndex,
    onZoom,
    onZoomStateChange,
    onClick,
    isDesktop = false,
    children,
}: ZoomableSlideProps): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const quickPinchZoomRef = useRef<QuickPinchZoom>(null);
    const zoomInEventFired = useRef(false);
    const isZoomed = useRef(false);
    const prevScale = useRef(1);
    const tapTimeoutRef = useRef<number | null>(null);
    const hasDraggedRef = useRef(false);
    const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);

    const cancelPendingTap = useCallback(() => {
        if (tapTimeoutRef.current !== null) {
            window.clearTimeout(tapTimeoutRef.current);
            tapTimeoutRef.current = null;
        }
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
        hasDraggedRef.current = false;
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!mouseDownPosRef.current) return;
        const dx = e.clientX - mouseDownPosRef.current.x;
        const dy = e.clientY - mouseDownPosRef.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
            hasDraggedRef.current = true;
        }
    }, []);

    const handleClick = useCallback(() => {
        if (hasDraggedRef.current) {
            hasDraggedRef.current = false;
            return;
        }
        if (tapTimeoutRef.current !== null) {
            // Second tap/click within the debounce window — discard it so only one action
            // fires per gesture (prevents double zoom on desktop, avoids unintended UI toggle on mobile).
            cancelPendingTap();
            return;
        }
        tapTimeoutRef.current = window.setTimeout(() => {
            tapTimeoutRef.current = null;
            onClick();
            if (!isDesktop) return;
            const currentZoom = quickPinchZoomRef.current;
            const wrapper = containerRef.current;
            if (!currentZoom || !wrapper) return;

            if (isZoomed.current) {
                // Zoom out to center using offsetWidth/Height — safe at any scale.
                currentZoom.alignCenter({
                    x: wrapper.offsetWidth / 2,
                    y: wrapper.offsetHeight / 2,
                    scale: 1,
                });
            } else {
                // Zoom in to click point. getBoundingClientRect() is safe here
                // because scale is 1 — the transform is identity so rect dimensions
                // match the natural layout size.
                const rect = wrapper.getBoundingClientRect();
                currentZoom.alignCenter({
                    x: (mouseDownPosRef.current?.x ?? 0) - rect.left,
                    y: (mouseDownPosRef.current?.y ?? 0) - rect.top,
                    scale: MAX_ZOOM,
                });
            }
        }, 300);
    }, [cancelPendingTap, onClick, isDesktop]);

    const resetZoom = useCallback(() => {
        const wrapper = containerRef.current;
        const currentZoom = quickPinchZoomRef.current;
        if (wrapper && currentZoom) {
            // reset inactive slide back to centre
            currentZoom.alignCenter({
                x: wrapper.offsetWidth / 2,
                y: wrapper.offsetHeight / 2,
                scale: 1,
            });
            zoomInEventFired.current = false;
            if (isZoomed.current) {
                isZoomed.current = false;
                onZoomStateChange(false);
            }
            if (isDesktop) {
                wrapper.classList.remove(styles.zoomOutCursor);
            }
            prevScale.current = 1;
        }
    }, [onZoomStateChange, isDesktop]);

    const onUpdate = useCallback(
        ({ x, y, scale }: { x: number; y: number; scale: number }) => {
            const wrapper = containerRef.current;
            if (wrapper) {
                wrapper.style.setProperty("transform", make3dTransformValue({ x, y, scale }));
                const direction =
                    scale > prevScale.current
                        ? ZoomDirection.in
                        : scale < prevScale.current
                        ? ZoomDirection.out
                        : null;

                if (direction === ZoomDirection.in && !zoomInEventFired.current) {
                    zoomInEventFired.current = true;
                    onZoom(ZoomDirection.in);
                } else if (direction === ZoomDirection.out && zoomInEventFired.current) {
                    zoomInEventFired.current = false;
                    onZoom(ZoomDirection.out);
                }

                const nowZoomed = scale > 1;
                if (nowZoomed !== isZoomed.current) {
                    isZoomed.current = nowZoomed;
                    onZoomStateChange(nowZoomed);
                }

                if (isDesktop) {
                    wrapper.classList.toggle(styles.zoomOutCursor, scale >= MAX_ZOOM);
                }

                prevScale.current = scale;
            }
        },
        [onZoom, onZoomStateChange, isDesktop]
    );

    useEffect(() => {
        if (index !== currentSlideIndex) {
            cancelPendingTap();
            resetZoom();
        }
    }, [currentSlideIndex, index, cancelPendingTap, resetZoom]);

    return (
        <QuickPinchZoom
            ref={quickPinchZoomRef}
            onUpdate={onUpdate}
            {...(!isDesktop && {
                onDoubleTap: cancelPendingTap,
                doubleTapZoomOutOnMaxScale: true,
            })}
            draggableUnZoomed={false}
            maxZoom={MAX_ZOOM}
            tapZoomFactor={isDesktop ? 0 : undefined}
            inertia={false}
        >
            <div
                ref={containerRef}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                role="none"
            >
                {children}
            </div>
        </QuickPinchZoom>
    );
}
